import * as React from "react";
import { useMutation } from "@tanstack/react-query";
import type { useRouter } from "@tanstack/react-router";

import type { RegisteredUser } from "@workspace/profile";

import { authApi } from "./lib/api/authApi";
import {
	getStoredRefreshToken,
	getStoredToken,
	getStoredUser,
	setStoredRefreshToken,
	setStoredToken,
	setStoredUser,
} from "./storage";

export interface AuthContext {
	userId: string | null;
	isAuthenticated: boolean;
	user: RegisteredUser | null;
	login: (
		newProfile: RegisteredUser,
		provider: string,
		token: string,
		skipInvalidate?: boolean,
	) => Promise<void>;
	logout: () => Promise<void>;
	refresh: () => Promise<boolean>;
	setUser: React.Dispatch<React.SetStateAction<RegisteredUser | null>>;
	token: () => string | null;
}

export const AuthContext = React.createContext<AuthContext | null>(null);

export function useAuthContext({
	router,
}: {
	router: ReturnType<typeof useRouter>;
}) {
	const [user, setUser] = React.useState<RegisteredUser | null>(() =>
		getStoredUser(),
	);
	const getJwt = useMutation({
		...authApi.exchangeTokenOptions(),
	});

	const { mutateAsync } = getJwt;

	const userId = user?.id || null;
	const isAuthenticated = !!userId;

	const logout = React.useCallback(async () => {
		const storedRefresh = getStoredRefreshToken();
		if (storedRefresh) {
			await authApi.revokeRefresh(storedRefresh);
			setStoredRefreshToken(null);
		}
		router.navigate({
			to: "/logout",
		});
		await router.invalidate();
	}, [router]);

	const login = React.useCallback(
		async (
			newProfile: RegisteredUser,
			provider: string,
			token: string,
			skipInvalidate: boolean = false,
		) => {
			// The profile handed in by an external strategy carries the PROVIDER's
			// subject as its id, which is not necessarily the app user id (see
			// authApi.resolveUser). Resolve the app user before exchanging, since the
			// exchange loads the RegisteredUser aggregate by the id it is given — a
			// provider subject that diverges resolves nothing and the user is left
			// without an app token. Resolving also yields the user's true
			// registrationStatus rather than whatever the strategy assumed.
			const appUser =
				(await authApi.resolveUser(provider, token)) ?? newProfile;

			const result = await mutateAsync({
				userId: appUser.id,
				provider,
				token,
			});

			setStoredToken({ token: result.token, expires: result.expires });
			setStoredRefreshToken(result.refreshToken);
			setStoredUser(appUser);
			setUser(appUser);

			if (skipInvalidate) return;

			await router.invalidate();
		},
		[mutateAsync, router],
	);

	const refresh = React.useCallback(async () => {
		const storedRefresh = getStoredRefreshToken();
		if (!storedRefresh) return false;

		try {
			const result = await authApi.refresh(storedRefresh);
			setStoredToken({ token: result.token, expires: result.expires });
			setStoredRefreshToken(result.refreshToken);
			return true;
		} catch {
			setStoredToken({ token: "", expires: new Date(0) });
			setStoredRefreshToken(null);
			return false;
		}
	}, []);

	React.useEffect(() => {
		const invalidateRouter = async () => {
			setUser(getStoredUser());
			await router.invalidate();
		};
		invalidateRouter();
	}, [router]);

	return {
		userId,
		isAuthenticated,
		user: user || null,
		login,
		logout,
		refresh,
		setUser,
		token: () => getStoredToken(),
	};
}

export function useAuth() {
	const context = React.use(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}
